export function SummaryRenderer({ text }) {
  if (!text) return null;

  const getStyle = (h) => {
    if (h.startsWith('📅')) return { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', dot: 'bg-blue-500' };
    if (h.startsWith('🟢')) return { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', dot: 'bg-green-500' };
    if (h.startsWith('⚠️')) return { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500' };
    if (h.startsWith('💊')) return { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', dot: 'bg-purple-500' };
    if (h.startsWith('📈')) return { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', dot: 'bg-blue-500' };
    if (h.startsWith('🚨')) return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', dot: 'bg-red-500' };
    if (h.startsWith('✅')) return { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', dot: 'bg-green-500' };
    if (h.startsWith('🩺')) return { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', dot: 'bg-blue-500' };
    if (h.startsWith('💡')) return { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', dot: 'bg-yellow-500' };
    if (h.startsWith('📊')) return { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', dot: 'bg-indigo-500' };
    return { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', dot: 'bg-gray-400' };
  };

  // Clean bold markers
  const cleanText = (t) => t
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/^[-•*#]+\s*/, '')
    .trim();

  // Check if text has emoji sections
  const hasEmoji = /📅|🟢|⚠️|💊|📈|🚨|✅|🔴|📊|🩺|💡/.test(text);

  if (hasEmoji) {
    const sections = text.split(/(?=📅|🟢|⚠️|💊|📈|🚨|✅|🔴|📊|🩺|💡)/g).filter(s => s.trim());
    return (
      <div className="space-y-3">
        {sections.map((section, i) => {
          const lines = section.trim().split('\n').filter(l => l.trim());
          if (!lines.length) return null;
          const header = lines[0].trim();
          const style = getStyle(header);
          const points = lines.slice(1)
            .map(l => cleanText(l))
            .filter(l => l.length > 0);

          return (
            <div key={i} className={`${style.bg} border ${style.border} rounded-2xl p-4`}>
              <p className={`text-xs font-black ${style.text} mb-2.5`}>{header}</p>
              {points.length > 0 && (
                <div className="space-y-2">
                  {points.map((point, j) => (
                    <div key={j} className="flex items-start gap-2.5">
                      <div className={`w-1.5 h-1.5 ${style.dot} rounded-full mt-1.5 shrink-0`}></div>
                      <p className="text-xs text-gray-700 leading-relaxed">{point}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // No emoji — parse line by line into clean sections
  const lines = text.split('\n').filter(l => l.trim());
  const blocks = [];
  let currentBlock = null;

  lines.forEach(line => {
    const cleaned = line.trim();

    // Detect heading (##, bold, or ALL CAPS short line)
    const isHeading =
      cleaned.startsWith('##') ||
      cleaned.startsWith('#') ||
      (cleaned.startsWith('**') && cleaned.endsWith('**')) ||
      (cleaned.length < 50 && cleaned === cleaned.toUpperCase() && cleaned.length > 3);

    // Detect bullet point
    const isBullet = /^[-•*]\s/.test(cleaned) || /^\d+\.\s/.test(cleaned);

    if (isHeading) {
      if (currentBlock) blocks.push(currentBlock);
      currentBlock = {
        type: 'section',
        header: cleanText(cleaned),
        points: []
      };
    } else if (isBullet) {
      if (!currentBlock) currentBlock = { type: 'section', header: '', points: [] };
      currentBlock.points.push(cleanText(cleaned));
    } else if (cleaned.length > 0) {
      // Regular paragraph line — treat as bullet under current block
      if (!currentBlock) currentBlock = { type: 'section', header: '', points: [] };
      // Split by sentence if long paragraph
      if (cleaned.length > 100) {
        const sentences = cleaned.split(/(?<=[.!?])\s+/).filter(s => s.trim());
        sentences.forEach(s => {
          if (currentBlock.points) currentBlock.points.push(s.trim());
        });
      } else {
        currentBlock.points.push(cleaned);
      }
    }
  });

  if (currentBlock) blocks.push(currentBlock);

  const colors = [
    { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', dot: 'bg-blue-500' },
    { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', dot: 'bg-green-500' },
    { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', dot: 'bg-purple-500' },
    { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500' },
    { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', dot: 'bg-indigo-500' },
  ];

  return (
    <div className="space-y-3">
      {blocks.map((block, i) => {
        const style = colors[i % colors.length];
        return (
          <div key={i} className={`${style.bg} border ${style.border} rounded-2xl p-4`}>
            {block.header && (
              <p className={`text-xs font-black ${style.text} mb-2.5 uppercase tracking-wide`}>{block.header}</p>
            )}
            <div className="space-y-2">
              {block.points.map((point, j) => (
                <div key={j} className="flex items-start gap-2.5">
                  <div className={`w-1.5 h-1.5 ${style.dot} rounded-full mt-1.5 shrink-0`}></div>
                  <p className="text-xs text-gray-700 leading-relaxed">{point}</p>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}